import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import {
  IApplicationState,
  IFilterItem
} from "../../store";
import { dateUtcOnly, getAuctionTimezone, nameof } from "../../core";
import {
  ExchangeTypeEnum,
  FeatureHeadings,
  IAuction,
  IBook,
} from "../../services";
import {
  DefaultLayout,
  GridCheckboxFilterPanel,
  ICheckBoxFilter,
  ICheckBoxValueEvent,
  ToggleRadio,
  GridDateTimeRenderer
} from "../../components";
import { useTheme } from "styled-components";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  RowClassParams,
} from "ag-grid-community";
import moment from "moment-timezone";
import Grid from "../../components/Grid/Grid";
import { Dispatch } from "redux";
import {
  IUpdateAuctionCalendarCorridorFilterValueAction,
  IUpdateAuctionCalendarExchangeFilterValueAction,
  IUpdateAuctionCalendarInterconnectorFilterValueAction,
  IUpdateAuctionCalendarProductFilterValueAction,
} from "../../store/AuctionCalendar";
import { 
  UpateAuctionCalendarCorridorFilterValueAction,
  UpateAuctionCalendarInterconnectorFilterValueAction,
  UpateAuctionCalendarIsShowFilterValueAction,
  UpateAuctionCalendarProductFilterValueAction,
  UpdateAuctionCalendarExchangeFilterValueAction,
} from "../../store/AuctionCalendar/ActionCreator";
import { FetchBookAction } from "../../store/Book/ActionCreator";
import { product, showHide } from ".";
import { LoadingIndicator } from "../../components/LoadingIndicator/LoadingIndicator";
import { FetchFiltersAndAuctionCalendarAction } from "../../store/AuctionCalendar/ActionCreator";
import { CapacityReturnsRenderer } from "./CapacityReturnsRenderer";
import { AuctionCalendarContainer, AuctionCalendarStyles } from "./AuctionCalendar.styled";

interface IAuctionProps {}
interface IAuctionPanelItem {
  options: string[];
  value: string;
  onShowChange: any;
}

const allSelected = (data?: IFilterItem[]) => {
  if (!data || data.find((x) => !x.isSelected)) {
    return false;
  }
  return true;
};

export const buildGridCheckBoxFilter = (
  action: (
    currentCorridors: IFilterItem[],
    updateValue: IFilterItem
  ) =>
    | IUpdateAuctionCalendarCorridorFilterValueAction
    | IUpdateAuctionCalendarInterconnectorFilterValueAction
    | IUpdateAuctionCalendarProductFilterValueAction
    | IUpdateAuctionCalendarExchangeFilterValueAction,
  name: string,
  data?: IFilterItem[],
  gridApi?: GridApi | null,
  dispatch?: Dispatch<any>
) => {

  if (data && data.length > 0) {
    const valueEvent = data.map((item: IFilterItem) => {
      return {
        value: item.value,
        isChecked: item.isSelected,
        color: item.color,
        event: (result: any) => {
          if (gridApi && data && dispatch) {
            const itemTarget = result?.target;
            dispatch(
              action(data, {
                value: itemTarget?.value,
                isSelected: itemTarget?.checked,
                color: item?.color,
              } as IFilterItem)
            );
          }
        },
      } as ICheckBoxValueEvent<string>;
    });

    const all = {
      value: "All",
      isChecked: allSelected(data),
      event: (result: any) => {
        if (gridApi && data && dispatch) {
          const item = result?.target;
          dispatch(
            action(data, {
              value: item?.value,
              isSelected: item?.checked,
            } as IFilterItem)
          );
        }
      },
    } as ICheckBoxValueEvent<string>;

    const filters = {
      columnName: name,
      valueEvent,
      all,
    } as ICheckBoxFilter<string>;
    return filters;
  }
};

export const filterByFilterItems = (
  node: IRowNode,
  filterName: string,
  filterItems?: IFilterItem[]
) => {
  const selected = filterItems?.filter((x) => x.isSelected).map((c) => c.value);
  return (
    selected !== undefined &&
    selected &&
    node.data.hasOwnProperty(filterName) &&
    selected.includes(node.data[filterName])
  );
};

export const filterByProduct = (node: IRowNode, filterItemsArg?: IFilterItem[]) => {
  const filterItems = filterItemsArg || [];
  const allSelected  = filterItems.length === filterItems.filter((x) => x.isSelected).length;

  if (allSelected ) {
    return true;
  }

  const isMonthly =
    filterItems?.find(
      (x) =>
        x.isSelected === true &&
        x.value.toLowerCase() === product.monthly.toLowerCase()
    ) && node.data.product.toLowerCase() === product.monthly.toLowerCase();

  if (isMonthly) {
    return true;
  }

  const isSeasonal =
    filterItems?.find(
      (x) =>
        x.isSelected === true &&
        x.value.toLowerCase() === product.seasonal.toLowerCase()
    ) && node.data.product.toLowerCase() === product.seasonal.toLowerCase();

  if (isSeasonal) {
    return true;
  }
  const isQuarterly =
    filterItems?.find(
      (x) =>
        x.isSelected === true &&
        x.value.toLowerCase() === product.quarterly.toLowerCase()
    ) && node.data.product.toLowerCase() === product.quarterly.toLowerCase();

  if (isQuarterly) {
    return true;
  }
  const isYearly =
    filterItems?.find(
      (x) =>
        x.isSelected === true &&
        x.value.toLowerCase() === product.yearly.toLowerCase()
    ) && node.data.product.toLowerCase() === product.yearly.toLowerCase();

  if (isYearly) {
    return true;
  }

  const isWeekly =
    filterItems?.find(
      (x) =>
        x.isSelected === true &&
        x.value.toLowerCase() === product.weekly.toLowerCase()
    ) &&
    ( node.data.product.toLowerCase() === product.weekly.toLowerCase());

  if (isWeekly) {
    return true;
  }

  const isWeekend =
    filterItems?.find(
      (x) =>
        x.isSelected === true &&
        x.value.toLowerCase() === product.weekend.toLowerCase()
    ) &&
    ( node.data.product.toLowerCase() === product.weekend.toLowerCase());

  if (isWeekend) {
    return true;
  }

  return false;
};

const isDefined = (value: any | null | undefined) => {
  return value !== null && value !== undefined;
};

const timezone = getAuctionTimezone();
const dateTimeFormatter = (params: ICellRendererParams) => {

  if (params.value == null)
    return "";
  return isDefined(moment(params.value)?.tz(timezone))
    ? moment(params.value).tz(timezone).format("DD MMM YYYY - HH:mm:ss")
    : "";
}

type ColDefs = ColDef<any,any>[];

// grid
const auctionColumns = (isEnabledCapacityReturn: any): ColDefs => {
  const columns = [
    {
      field: nameof<IAuction>("exchangeCode"),
      sortable: true,
      flex: 0.8,
      cellRenderer: undefined as any,
    },
    { field: nameof<IAuction>("interconnector"), headerName: "IC", flex: 0.4 },
    { field: nameof<IAuction>("corridor"), flex: 0.5 },
    { field: nameof<IAuction>("product"), flex: 0.6 },
    { field: nameof<IAuction>("auctionName"), flex: 1 },
    {
      field: nameof<IAuction>("periodStart"),
      valueFormatter: dateTimeFormatter,
      flex: 1,
    },
    {
      field: nameof<IAuction>("periodStop"),
      headerName: "Period End",
      valueFormatter: dateTimeFormatter,
      flex: 1,
    },
    {
      field: nameof<IAuction>("biddingOpen"),
      cellRendererParams: GridDateTimeRenderer,
      valueFormatter: dateTimeFormatter,
      sort: "asc",
      flex: 1,
    },
    {
      field: nameof<IAuction>("biddingClose"),
      cellRendererParams: GridDateTimeRenderer,
      valueFormatter: dateTimeFormatter,      
      flex: 1,
    },
    {
      field: nameof<IAuction>("capacityReturnGateOpen"),
      headerName: "Return Open",
      cellRendererParams: GridDateTimeRenderer,
      valueFormatter: dateTimeFormatter,
      flex: 1,
    },
    {
      field: nameof<IAuction>("capacityReturnGateClose"),
      headerName: "Return Close",
      cellRendererParams: GridDateTimeRenderer,
      valueFormatter: dateTimeFormatter,
      flex: 1,
    }
  ];

  if (isEnabledCapacityReturn) {
    const returnCapacityColumn = {
      field: nameof<IAuction>("capacityReturn"),
      headerName: "Return Capacity",
      cellRenderer: CapacityReturnsRenderer as any,
      flex: 1,
    };

    columns.push(returnCapacityColumn);
  }

  return columns as ColDef[];
};

const AuctionCalendar: React.FC<IAuctionProps> = () => {
  const dispatch = useAppDispatch();
  const isLoading = useSelector((state: IApplicationState) => state.auctionCalendar.loading);
  const data = useSelector((state: IApplicationState) => state.auctionCalendar.data);
  const filters = useSelector((state: IApplicationState) => state.auctionCalendar.currentFilters);
  const exceptionMessage = useSelector((state: IApplicationState) => state.auctionCalendar.exceptionMessage);
  const refFilters = useRef(filters);
  const [rowData, setRowData] = useState<IAuction[]>(data || []);
  // Feature flag enabled by default after LaunchDarkly removal
  const enableCapacityReturns = true;
  
  const gridOptions: GridOptions = { 
    rowHeight: 40, 
    headerHeight: 36,
    rowSelection: 'single',
    suppressRowDeselection: true,
  };
  const theme = useTheme();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const gridRef = useRef<GridApi | null>(null);
  const options = [showHide.show, showHide.hide];
  const [show, setShow] = useState(filters?.isShow !== false ? showHide.show : showHide.hide);
  const [alertMessage, setAlertMessage] = useState("");
  const books = useSelector((state: IApplicationState) => state.book.books);
  const [selectedAuctionId, setSelectedAuctionId] = useState<string | null>(null);
  const isReturnsPanelVisible = useSelector((state: IApplicationState) => state.returns.showReturnsPanel);
  
  useEffect(() => {
    if (selectedAuctionId && gridApi) {
      gridApi.forEachNode(node => {
        if (node.data?.identification === selectedAuctionId) {
          node.setSelected(true);
          if (node.rowIndex !== null && node.rowIndex !== undefined) {
            gridApi.ensureIndexVisible(node.rowIndex, 'top');
          }
        }
      });
    }
  }, [selectedAuctionId, gridApi]);

  const getColumns: () => ColDefs = useCallback(() => {
    return auctionColumns(enableCapacityReturns) as ColDefs;
  }, [enableCapacityReturns]);

  useEffect(() => {
    if (!data) {
      dispatch(FetchFiltersAndAuctionCalendarAction());
    }
  }, [data, dispatch]);

  useEffect(() => {
    if (typeof filters !== "undefined") {
      var todaysDate = dateUtcOnly(new Date(), getAuctionTimezone());
      var auctionStartDateUtc = filters.startDateUtc;
      if (
        auctionStartDateUtc !== null &&
        typeof auctionStartDateUtc !== "undefined"
      ) {
        if (auctionStartDateUtc < todaysDate) {
          filters.exchanges = [
            { value: ExchangeTypeEnum.Jao.toUpperCase(), isSelected: true },
            { value: ExchangeTypeEnum.Empire.toUpperCase(), isSelected: true },
          ];
        }
      }
    }
    setRowData(data || []);
  }, [data, gridApi]);

  useEffect(() => {
    if (gridApi) {
      refFilters.current = filters;
      gridApi.onFilterChanged();
    }
  }, [filters, gridApi]);

  useEffect(() => {
    if (exceptionMessage) {
      setAlertMessage(exceptionMessage);
    }
  }, [exceptionMessage]);  

  useEffect(() => {
    if (!books || books.length === 0) {
      dispatch(FetchBookAction());
    }
  }, [books, dispatch]);

  useEffect(() => {
    if (books) {
      data?.forEach((x) => {
        x.books = books.map((y) => {
          return { code: y.code, name: y.name } as IBook;
        });
      });
    }
  }, [books, data]);

  // Clear the selected auction when the Returns panel is closed
  useEffect(() => {
    if (!isReturnsPanelVisible) {
      setSelectedAuctionId(null);
    }
  }, [isReturnsPanelVisible]);

  const corridorCheckBoxFilter = buildGridCheckBoxFilter(
    UpateAuctionCalendarCorridorFilterValueAction,
    "Corridor".toUpperCase(),
    filters?.corridors,
    gridApi,
    dispatch
  );

  const productCheckBoxFilter = buildGridCheckBoxFilter(
    UpateAuctionCalendarProductFilterValueAction,
    "Product".toUpperCase(),
    filters?.products,
    gridApi,
    dispatch
  );  

  const icCheckBoxrFilter = buildGridCheckBoxFilter(
    UpateAuctionCalendarInterconnectorFilterValueAction,
    "IC".toUpperCase(),
    filters?.interconnectors,
    gridApi,
    dispatch
  );

  const exchangeCheckBoxFilter = buildGridCheckBoxFilter(
    UpdateAuctionCalendarExchangeFilterValueAction,
    "Exchange".toUpperCase(),
    filters?.exchanges,
    gridApi,
    dispatch
  );

  const applyShowFilter = (selected: showHide) => {
    dispatch(UpateAuctionCalendarIsShowFilterValueAction(selected === showHide.show));

    if (gridApi) {
      setTimeout(() => {
        gridApi.onFilterChanged();
      }, 0);
    }
  }

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params.api);
    gridRef.current = params.api;
    applyShowFilter(show);
  };

  const filterAuctionCalendarData = (node: IRowNode): boolean => {
    if (refFilters.current?.isShow !== false) {
      return (
        filterByFilterItems(node, nameof<IAuction>("exchangeCode"), refFilters.current?.exchanges) &&
        filterByFilterItems(node, "interconnector", refFilters.current?.interconnectors) &&
        filterByProduct(node, refFilters.current?.products)
      );
    }

    return (
      filterByFilterItems(node, "corridor", refFilters.current?.corridors) &&
      node.data["interconnector"] === "N/A" &&
      filterByProduct(node, refFilters.current?.products) &&
      filterByFilterItems(node, nameof<IAuction>("exchangeCode"), refFilters.current?.exchanges)
    );
  };

  const PanelItem: React.FC<IAuctionPanelItem> = (props) => {
    return (
      <div>
        <GridCheckboxFilterPanel
          data={exchangeCheckBoxFilter}
          idPrefix="exchange"
        ></GridCheckboxFilterPanel>
        <GridCheckboxFilterPanel
          data={productCheckBoxFilter}
          idPrefix="product"
        />
        <ToggleRadio 
          showOrHide={show === showHide.show ? "show" : "hide"} 
          onChange={onShowChange}
        />
        <GridCheckboxFilterPanel
          disabled={show !== showHide.show}
          data={icCheckBoxrFilter}
          idPrefix="interconnector"
        />
        <GridCheckboxFilterPanel
          disabled={show !== showHide.hide}
          data={corridorCheckBoxFilter}
          idPrefix="corridor"
        />
      </div>
    );
  };

  const onShowChange = () => {
    const updated =
      show === showHide.show
      ? showHide.hide
      : showHide.show;
      
    setShow(updated);
    applyShowFilter(updated);
  };

  const columns = getColumns();

  return (
    <AuctionCalendarContainer data-testid="AuctionCalendar">
      <AuctionCalendarStyles />
      <DefaultLayout
        panel={
          <PanelItem
            value={show}
            options={options}
            onShowChange={onShowChange}
          />
        }
        title={FeatureHeadings.Auctions}
        message={alertMessage}
        children={
          isLoading ? (
            <LoadingIndicator />
          ) : (
              <Grid
              columns={columns}
              rowData={rowData}
              theme={theme}
              onGridEvent={onGridReady}
              hasExternalFilter={true}
              filterFunction={filterAuctionCalendarData}
              gridOptions={{
                ...gridOptions,
                context: { setSelectedAuctionId },
                getRowClass: (params: RowClassParams) => {
                  return params.data.identification === selectedAuctionId ? 'selected-auction-row' : '';
                }
              }}
            />
          )
        }
      />
    </AuctionCalendarContainer>
  );
};

export default AuctionCalendar;